import React from "react";
import PropTypes from "prop-types";
import { capitalizarTexto } from "../../../shared/utils/formatador";
import {
  ehEventoObito,
  formatarDataHoraUtcPtBr,
} from "../utils/formatarTimeline";
import { ordenarPorLiberacaoEProcessamento } from "../utils/ordenarJornada";

const TabJornadaPaciente = ({ paciente, dadosCompletos }) => {
  const jornadaPaciente = dadosCompletos?.jornadaPaciente || {};

  const jornadaColo = Array.isArray(jornadaPaciente.colo)
    ? jornadaPaciente.colo
    : [];
  const jornadaMama = Array.isArray(jornadaPaciente.mama)
    ? jornadaPaciente.mama
    : [];
  const jornadaGeral = Array.isArray(jornadaPaciente.geral)
    ? jornadaPaciente.geral
    : [];           

  const temRetorno = (registro = {}) => {
    return Boolean(
      registro.status ||
      registro.diretriz ||
      registro.descricao ||
      registro.dataProcessamento ||
      registro.dataLiberacao,
    );
  };

  const jornadaColoFonte = [...jornadaGeral, ...jornadaColo];
  const jornadaMamaFonte = [...jornadaGeral, ...jornadaMama];

  const jornadaColoComRetorno = jornadaColoFonte
    .filter(temRetorno)
    .map((item, index) => ({
      id: `colo-${item.dataProcessamento || index}`,
      status: item.status,
      diretriz: item.diretriz,
      descricao: (capitalizarTexto(item.descricao)),
      dataProcessamento: item.dataProcessamento,
      dataLiberacao: item.dataLiberacao,
    }))
    .sort(ordenarPorLiberacaoEProcessamento);

  const jornadaMamaComRetorno = jornadaMamaFonte
    .filter(temRetorno)
    .map((item, index) => ({
      id: `mama-${item.dataProcessamento || index}`,
      status: item.status,
      diretriz: item.diretriz,
      descricao: (capitalizarTexto(item.descricao)),
      dataProcessamento: item.dataProcessamento,
      dataLiberacao: item.dataLiberacao,
    }))
    .sort(ordenarPorLiberacaoEProcessamento);

  const renderJornadaColuna = (titulo, lista, mensagemVazia) => {
    return (
      <div className="col-12 col-xl-6">
        <div className="card border-0 shadow-sm p-3 bg-body-tertiary rounded h-100">
          <div className="card-header border-0 d-flex justify-content-between align-items-center jornada-coluna-header">
            <h5 className="mb-0 text-dark">{titulo}</h5>
          </div>

          <div className="card-body pt-3">
            {lista.length === 0 && (
              <div className="small text-muted rounded-3 p-3 jornada-coluna-vazia">
                {mensagemVazia}
              </div>
            )}

            {lista.length > 0 && (
              <div className="position-relative jornada-timeline">
                <div className="jornada-timeline-linha" />

                {lista.map((item, index) => {
                  const isMaisRecente = index === 0;
                  const isEventoObito = ehEventoObito(item);
                  const classeBolinha = isMaisRecente
                    ? "jornada-timeline-bolinha jornada-timeline-bolinha-mais-recente"
                    : "jornada-timeline-bolinha";

                  return (
                    <div
                      key={item.id || `${item.dataProcessamento}-${index}`}
                      className="position-relative mb-3 ps-4"
                    >
                      <span className={classeBolinha} />

                      <div className="card border-0 rounded-3 jornada-card-evento">
                        <div className="card-body py-2 px-3">
                          <div className="d-flex flex-wrap justify-content-between align-items-center mb-2">
                            <strong className="text-dark fs-6">
                              {item.status || " - "}
                            </strong>
                          </div>

                          <div className="small text-muted mb-1">
                            {item.diretriz || " - "}
                          </div>
                          <div className="small text-muted mb-2">
                            {item.descricao || " - "}
                          </div>

                          {!isEventoObito && (
                            <div className="small text-secondary">
                              <b>Liberação:</b>{" "}
                              {formatarDataHoraUtcPtBr(item.dataLiberacao)}
                            </div>
                          )}

                          <div className="small text-secondary">
                            <b>Processamento:</b>{" "}
                            {formatarDataHoraUtcPtBr(item.dataProcessamento)}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="jornada-paciente">
      <div className="informacoes-pessoais p-3 d-grid gap-4">
        <div className="row g-3">
          <div className="col-12 col-md-6">
            <div className="text-muted small mb-1">Paciente</div>
            <div className="fw-semibold text-dark">
              {capitalizarTexto(paciente.nome) || "—"}
            </div>
          </div>

          <div className="col-6 col-md-6">
            <div className="text-muted small mb-1">ID</div>
            <div className="fw-semibold">{paciente.id || "—"}</div>
          </div>
        </div>
      </div>

      <div className="card border-0 rounded-3 mb-4 jornada-card-principal">
        <div className="card-body pt-0">
          <div className="row g-3 mt-1">
            {renderJornadaColuna(
              "Colo do útero",
              jornadaColoComRetorno,
              "Sem retorno nesta coluna.",
            )}
            {renderJornadaColuna(
              "Mama",
              jornadaMamaComRetorno,
              "Sem retorno nesta coluna.",
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

TabJornadaPaciente.propTypes = {
  patientId: PropTypes.number,
  paciente: PropTypes.object,
  dadosCompletos: PropTypes.object,
};

export default TabJornadaPaciente;
